import React, { useEffect, useState, createContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Provider, useSelector, useDispatch } from 'react-redux';

// Store
import store from './state';

// Auth Verifier component
import AuthVerifier from './components/AuthVerifier';

// Socket Manager
import SocketManager from './socket/SocketManager';

// Profile Photo Components
import ProfilePhotoDialog from './components/ProfilePhotoDialog';
import { ProfilePhotoProvider, useProfilePhoto } from './context/ProfilePhotoContext';

// Pages
import Home from './pages/home/index';
import Login from './pages/login/index';
import SignUp from './pages/signup/index';
import Verification from './pages/signup/Verification';
import ForgotPassword from './pages/login/ForgotPassword';
import Profile from './pages/profile/index';
import Calendar from './pages/calendar/index';
import Service from './pages/service/index';
import Store from './pages/store/index';
import ProviderProfile from './pages/service/Providers/ProviderProfile';
import ManageService from './pages/service/Services/ManageService';
import Landing from './pages/landing/index';
import Notification from './pages/notifications/index';
import ConnectDashboard from './pages/connect/index';
import FoodRoutes from './routes/FoodRoutes';
import StoreRoutes from './routes/StoreRoutes';
import About from './pages/landing/about';
import Contact from './pages/landing/contact';
import PrivacyPolicy from './pages/landing/privacy';
import TermsOfService from './pages/landing/terms';
import MealPlanner from './pages/meal-planner/index';

// Feature Detail Pages
import SmartCalendarFeature from './pages/landing/features/smart-calendar';
import SocialNetworkFeature from './pages/landing/features/social-network';
import ComingSoonFeature from './pages/landing/features/coming-soon';
import AIMealPlannerFeature from './pages/landing/features/ai-meal-planner';

// ScrollToTop component
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Create AppContext for sharing state between components
export const AppContext = createContext({
  showCalendarInvitationDialog: false,
  setShowCalendarInvitationDialog: () => {},
  notificationData: null,
  setNotificationData: () => {},
});

// Protected route wrapper component
const ProtectedRoute = ({ children }) => {
  const { token, user } = useSelector(state => state.auth);
  const isAuthenticated = Boolean(token && user?.id);
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  return (
    <>
      {/* AuthVerifier only included for authenticated users */}
      <AuthVerifier />
      {children}
    </>
  );
};

const App = () => {
  return (
    <Provider store={store}>
      <ProfilePhotoProvider>
        <AppContent />
      </ProfilePhotoProvider>
    </Provider>
  );
};

// MainProfilePhotoDialog component to be used at the app level
const MainProfilePhotoDialog = () => {
  const { showProfilePhotoDialog, setShowProfilePhotoDialog } = useProfilePhoto();
  
  return (
    <ProfilePhotoDialog 
      isOpen={showProfilePhotoDialog} 
      onClose={() => setShowProfilePhotoDialog(false)} 
    />
  );
};

const AppContent = () => {
  const dispatch = useDispatch();
  const { theme } = useSelector(state => state.settings) || { theme: 'light' };
  const { token, user } = useSelector(state => state.auth);
  const isAuthenticated = Boolean(token && user?.id);
  
  // Add state for calendar invitation dialog
  const [showCalendarInvitationDialog, setShowCalendarInvitationDialog] = useState(false);
  const [notificationData, setNotificationData] = useState(null);
  
  // Check authentication on app load
  useEffect(() => {
    // Try to refresh token on app load if there's a refresh function
    if (typeof dispatch === 'function') {
      try {
        // Only attempt to refresh token if the function exists
        const authActions = store.getState()?.auth?.actions;
        if (authActions && authActions.refreshToken) {
          dispatch(authActions.refreshToken());
        }
      } catch (error) {
        console.error('Failed to refresh token:', error);
      }
    }
  }, [dispatch]);
  
  // Apply theme class to body
  useEffect(() => {
    // Add dark mode class to body if theme is dark
    if (theme === 'dark') {
      document.body.classList.add('dark');
    } else {
      document.body.classList.remove('dark');
    }
  }, [theme]);
  
  // Handle URL parameters on load to check if invitation dialog should be shown
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    if (queryParams.get('openInvitations') === 'true') {
      setShowCalendarInvitationDialog(true);
    }
  }, []);
  
  // Create context value for sharing state
  const contextValue = {
    showCalendarInvitationDialog,
    setShowCalendarInvitationDialog,
    notificationData,
    setNotificationData,
  };
  
  // Handle notification actions
  const handleNotificationAction = (actionType, data) => {
    if (actionType === 'navigate_calendar_with_dialog') {
      setNotificationData(data);
      setShowCalendarInvitationDialog(true);
      window.location.href = '/calendar';
    }
  };
  
  return (
    <div className={`app ${theme === 'dark' ? 'dark' : 'light'}`}>
      <AppContext.Provider value={contextValue}>
        <Router>
          <ScrollToTop />
          <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
            {/* AuthVerifier and SocketManager for authenticated users */}
            {isAuthenticated && (
              <>
                <AuthVerifier />
                <SocketManager />
                <MainProfilePhotoDialog />
              </>
            )}
            
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/login/forgot-password" element={<ForgotPassword />} />
              <Route path="/signup" element={<SignUp />} />
              <Route path="/verify" element={<Verification />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              
              {/* Feature detail pages */}
              <Route path="/feature/smart-calendar" element={<SmartCalendarFeature />} />
              <Route path="/feature/social-network" element={<SocialNetworkFeature />} />
              <Route path="/feature/coming-soon" element={<ComingSoonFeature />} />
              <Route path="/feature/ai-meal-planner" element={<AIMealPlannerFeature />} />
              
              {/* Protected routes with AuthVerifier */}
              <Route path="/home" element={
                <ProtectedRoute>
                  <Home onNotificationAction={handleNotificationAction} />
                </ProtectedRoute>
              } />
              <Route path="/profile" element={
                <ProtectedRoute>
                  <Profile onNotificationAction={handleNotificationAction} />
                </ProtectedRoute>
              } />
              <Route path="/calendar" element={
                <ProtectedRoute>
                  <Calendar onNotificationAction={handleNotificationAction} />
                </ProtectedRoute>
              } />
              <Route path="/meal-planner" element={
                <ProtectedRoute>
                  <MealPlanner onNotificationAction={handleNotificationAction} />
                </ProtectedRoute>
              } />
              <Route path="/service" element={
                <ProtectedRoute>
                  <Service onNotificationAction={handleNotificationAction} />
                </ProtectedRoute>
              } />
              <Route path="/service/manage" element={
                <ProtectedRoute>
                  <ManageService onNotificationAction={handleNotificationAction} />
                </ProtectedRoute>
              } />
              <Route path="/notifications" element={
                <ProtectedRoute>
                  <Notification onNotificationAction={handleNotificationAction} />
                </ProtectedRoute>
              } />
              <Route path="/provider/:id" element={
                <ProtectedRoute>
                  <ProviderProfile onNotificationAction={handleNotificationAction} />
                </ProtectedRoute>
              } />
              <Route path="/store" element={
                <ProtectedRoute>
                  <Store onNotificationAction={handleNotificationAction} />
                </ProtectedRoute>
              } />
              <Route path="/connect" element={
                <ProtectedRoute>
                  <ConnectDashboard onNotificationAction={handleNotificationAction} />
                </ProtectedRoute>
              } />
              
              {/* Route groups - these need to handle their own protection */}
              <Route path="/food/*" element={<FoodRoutes />} />
              <Route path="/store/*" element={<StoreRoutes />} />
              
              {/* Fallback redirect */}
              <Route path="*" element={<Navigate to={isAuthenticated ? "/home" : "/"} />} />
            </Routes>
          </div>
        </Router>
      </AppContext.Provider>
    </div>
  );
};

export default App;